import React, { useMemo, useState } from "react";
import {
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import PauseIcon from "@material-ui/icons/Pause";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import CadeteMap, { createMarkerIcon } from "./components/CadeteMap";
import CadeteSidePanel from "./components/CadeteSidePanel";
import { useCities } from "./hooks/useCities";
import { useCadeteTracking } from "./hooks/useCadeteTracking";
import { formatRelativeTime } from "./helpers/time";
import {
  normalizeCity,
  normalizeLocation,
  normalizeTrackingData,
  orderCadetesByLastTracking,
} from "./helpers/normalizers";
import { getV2 } from "../../api/axiosV2";
import "./mapa.css";

const Mapa = () => {
  const { cities, loading: loadingCities, error: citiesError } = useCities();
  const [selectedCityId, setSelectedCityId] = useState("");
  const [selectedCadeteId, setSelectedCadeteId] = useState(null);
  const [selectedCadeteDetail, setSelectedCadeteDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [viewFilter, setViewFilter] = useState("all");
  const [now, setNow] = useState(new Date());
  const [autoRefreshPaused, setAutoRefreshPaused] = useState(false);
  const [sortMode, setSortMode] = useState("lastTracking");

  const selectedCity = useMemo(
    () => normalizeCity(cities.find((city) => city.id === selectedCityId)),
    [cities, selectedCityId]
  );

  const {
    cadetes,
    nuevos,
    loading: loadingTracking,
    error: trackingError,
    lastUpdated,
    refetch,
  } = useCadeteTracking(selectedCityId, !autoRefreshPaused);

  const cadetesWithDetail = useMemo(() => {
    if (!selectedCadeteDetail) return cadetes;
    return cadetes.map((cadete) =>
      cadete.id === selectedCadeteDetail.id ? selectedCadeteDetail : cadete
    );
  }, [cadetes, selectedCadeteDetail]);

  const normalizedCadetes = useMemo(
    () => normalizeTrackingData(cadetesWithDetail),
    [cadetesWithDetail]
  );

  React.useEffect(() => {
    if (selectedCityId) return;
    const defaultCity = cities.find(
      (city) => city.nombre && city.nombre.toLowerCase() === "monte hermoso"
    );
    if (defaultCity) {
      setSelectedCityId(defaultCity.id);
    }
  }, [cities, selectedCityId]);

  const unassignedPedidos = useMemo(() => {
    return (nuevos || []).map((pedido) => ({
      ...pedido,
      origen: normalizeLocation(pedido.origen || {}) || pedido.origen,
      destino: normalizeLocation(pedido.destino || {}) || pedido.destino,
    }));
  }, [nuevos]);

  const orderedCadetes = useMemo(() => {
    if (sortMode === "mostOrders") {
      return [...normalizedCadetes].sort(
        (a, b) => (b.pedidos?.length || 0) - (a.pedidos?.length || 0)
      );
    }
    return orderCadetesByLastTracking(normalizedCadetes);
  }, [normalizedCadetes, sortMode]);

  const filteredCadetes = useMemo(() => {
    switch (viewFilter) {
      case "unassigned":
        return [];
      case "withoutOrders":
        return orderedCadetes.filter((cadete) => !(cadete.pedidos?.length > 0));
      case "withOrders":
        return orderedCadetes.filter((cadete) => cadete.pedidos?.length > 0);
      default:
        return orderedCadetes;
    }
  }, [orderedCadetes, viewFilter]);

  const unassignedToShow = useMemo(() => {
    if (viewFilter === "all" || viewFilter === "unassigned") return unassignedPedidos;
    return [];
  }, [unassignedPedidos, viewFilter]);

  const handleCityChange = (event) => {
    setSelectedCityId(event.target.value === "" ? "" : Number(event.target.value));
    setSelectedCadeteId(null);
    setSelectedCadeteDetail(null);
    setLoadingDetail(false);
  };

  const handleManualRefresh = () => {
    if (!selectedCityId) return;
    refetch(true);
  };

  const handleTogglePause = () => {
    setAutoRefreshPaused((prev) => !prev);
  };

  React.useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const handleViewFilterChange = (event) => {
    setViewFilter(event.target.value);
  };

  const handleSortChange = (mode) => {
    setSortMode(mode);
  };

  React.useEffect(() => {
    setSelectedCadeteDetail(null);
    if (!selectedCadeteId) return undefined;
    let isMounted = true;
    setLoadingDetail(true);
    getV2(`/admin/cadetes/tracking/realtime/${selectedCadeteId}`)
      .then((response) => {
        if (isMounted) {
          setSelectedCadeteDetail(response.data);
        }
      })
      .catch(() => {
        if (isMounted) {
          setSelectedCadeteDetail(null);
        }
      })
      .finally(() => {
        if (isMounted) setLoadingDetail(false);
      });
    return () => {
      isMounted = false;
    };
  }, [selectedCadeteId]);

  React.useEffect(() => {
    if (!selectedCadeteId) return;
    const exists = filteredCadetes.some((cadete) => cadete.id === selectedCadeteId);
    if (!exists) setSelectedCadeteId(null);
  }, [filteredCadetes, selectedCadeteId]);

  return (
    <Container className="mapa-container">
      <Typography variant="h5" gutterBottom>
        Mapa de cadetes
      </Typography>
      <Paper className="mapa-controls" elevation={1}>
        <FormControl variant="outlined" size="small" className="city-select">
          <InputLabel id="ciudad-select-label">Ciudad</InputLabel>
          <Select
            labelId="ciudad-select-label"
            label="Ciudad"
            value={selectedCityId}
            onChange={handleCityChange}
            disabled={loadingCities}
          >
            <MenuItem value="">
              <em>Seleccionar ciudad</em>
            </MenuItem>
            {cities.map((ciudad) => (
              <MenuItem key={ciudad.id} value={ciudad.id}>
                {ciudad.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" className="city-select">
          <InputLabel id="view-filter-label">Vista</InputLabel>
          <Select
            labelId="view-filter-label"
            label="Vista"
            value={viewFilter}
            onChange={handleViewFilterChange}
          >
            <MenuItem value="all">Mostrar todo</MenuItem>
            <MenuItem value="unassigned">Solo pedidos sin asignar</MenuItem>
            <MenuItem value="withoutOrders">Cadetes sin asignación</MenuItem>
            <MenuItem value="withOrders">Cadetes con asignación</MenuItem>
          </Select>
        </FormControl>

        <div className="mapa-status">
          {loadingCities && <span className="status-item">Cargando ciudades...</span>}
          {selectedCityId && loadingTracking && (
            <span className="status-item status-loading">
              <CircularProgress size={20} /> Actualizando tracking
            </span>
          )}
          {selectedCityId && (
            <div className="status-item">
              <IconButton size="small" onClick={handleTogglePause}>
                {autoRefreshPaused ? (
                  <PlayArrowIcon fontSize="small" />
                ) : (
                  <PauseIcon fontSize="small" />
                )}
              </IconButton>
              <IconButton size="small" onClick={handleManualRefresh}>
                <RefreshIcon fontSize="small" />
              </IconButton>
              {lastUpdated && !loadingTracking && (
                <Typography variant="caption" color="textSecondary">
                  Actualizado: {formatRelativeTime(lastUpdated, now)}
                </Typography>
              )}
            </div>
          )}
        </div>
      </Paper>

      {(citiesError || trackingError) && (
        <Paper className="mapa-error" elevation={0}>
          {citiesError || trackingError}
        </Paper>
      )}

      {selectedCity ? (
        <div className="map-and-panel">
          <Paper className="cadete-map-card" elevation={1}>
            <CadeteMap
              city={selectedCity}
              cadetes={filteredCadetes}
              selectedCadeteId={selectedCadeteId}
              loadingDetail={loadingDetail}
              unassignedOrders={unassignedToShow}
            />
          </Paper>
          <CadeteSidePanel
            cadetes={filteredCadetes}
            selectedCadeteId={selectedCadeteId}
            onSelectCadete={setSelectedCadeteId}
            sortMode={sortMode}
            onSortChange={handleSortChange}
          />
        </div>
      ) : (
        <Paper className="mapa-placeholder" elevation={0}>
          Selecciona una ciudad para ver el mapa y el listado de cadetes.
        </Paper>
      )}
    </Container>
  );
};

export default Mapa;
